import {
  WEIGHT,
  WEIGHT_LBS,
  AGE,
  SERUM,
  HEIGHT_CM,
  HEIGHT_IN,
  HEIGHT_GFR_CM,
  HEIGHT_GFR_IN,
  SERUM_GFR,
  FEET_RANGE,
  INCHES_RANGE,
  WEIGHT_RELAXED,
  WEIGHT_LBS_RELAXED,
  HEIGHT_CM_RELAXED,
  HEIGHT_IN_RELAXED,
} from "./constants.js";
import { getWeightInKg } from "../weightConverter.js";
import { feetInchesToCm, cmToFeetInches, getHeightInCm } from "../lengthConverter.js";
import { getCreatinineInMicromol } from "../creatinineConverter.js";

export const checkNum = (val, text) => {
  if (val === null || val === "") return `Please enter a value for ${text}`;
  const num = Number(val);
  if (isNaN(num)) return `Please enter only numeric characters in the ${text} field`;
  if (num < 0) return `Negative ${text} entered. Please enter valid ${text}`;
  return "";
};

const validateField = (value, range, fieldName, unit = "") => {
  if (!value) return `Please enter a value for ${fieldName}`;
  const num = Number(value);
  if (isNaN(num)) return `Please enter only numeric characters in the ${fieldName} field`;

  const minStr = unit ? `min: ${range.MIN} ${unit}` : `min: ${range.MIN}`;
  const maxStr = unit ? `max: ${range.MAX} ${unit}` : `max: ${range.MAX}`;
  if (num < range.MIN) {
    if (fieldName.toLowerCase().includes("serum creatinine")) {
      return `Serum creatinine value is too low (${minStr})`;
    }
    return `${fieldName} value is too low (${minStr})`;
  }
  if (num > range.MAX) {
    if (fieldName.toLowerCase().includes("serum creatinine")) {
      return `Serum creatinine value is too high (${maxStr})`;
    }
    return `${fieldName} value is too high (${maxStr})`;
  }
  return "";
};

export const saneAge = (age) => {
  const num = Number(age);
  if (isNaN(num)) return `Please enter only numeric characters in the age field`;
  if (num > AGE.MAX)
    return `Age older than ${AGE.MAX} years entered; please check age. For patients older than ${AGE.MAX} years, seek expert advice.`;
  if (num < AGE.MIN)
    return `Age entered is less than ${AGE.MIN} years. This calculator should only be used for patients older than ${AGE.MIN} years.`;
  return "";
};

export const saneWeight = (weight, unit = "kg") => {
  const num = Number(weight);
  if (isNaN(num)) return `Please enter only numeric characters in the weight field`;

  const weightKg = getWeightInKg(weight, unit);
  const maxWeight = unit === "kg" ? WEIGHT.MAX : WEIGHT_LBS.MAX;
  const minWeight = unit === "kg" ? WEIGHT.MIN : WEIGHT_LBS.MIN;
  const unitLabel = unit === "kg" ? "kg" : "lbs";

  if (weightKg > WEIGHT.MAX)
    return `Weight more than ${maxWeight}${unitLabel} entered; please check weight. For patients more than ${maxWeight}${unitLabel}, seek expert advice.`;
  if (weightKg < WEIGHT.MIN)
    return `Weight less than ${minWeight}${unitLabel} entered; please check weight. For patients less than ${minWeight}${unitLabel}, seek expert advice.`;
  return "";
};

export const saneHeight = (height, unit = "cm", range = null) => {
  const num = Number(height);
  if (isNaN(num)) return `Please enter only numeric characters in the height field`;

  const selectedRange = range || (unit === "cm" ? HEIGHT_CM : HEIGHT_IN);
  if (!selectedRange) return "Invalid height unit.";

  const unitSymbol = unit === "cm" ? "cm" : '"';

  if (num > selectedRange.MAX)
    return `Height more than ${selectedRange.MAX}${unitSymbol} entered; please check height. For patients taller than ${selectedRange.MAX}${unitSymbol}, seek expert advice.`;

  if (num < selectedRange.MIN)
    return `Height less than ${selectedRange.MIN}${unitSymbol} entered; please check height. For patients shorter than ${selectedRange.MIN}${unitSymbol}, seek expert advice.`;

  return "";
};

export const saneFeetInches = (feet, inches, cmRange = HEIGHT_CM) => {
  const feetNum = parseFloat(feet) || 0;
  const inchesNum = parseFloat(inches) || 0;

  if (feetNum === 0 && inchesNum === 0) {
    return "Please enter a value for height";
  }

  if (feet && feet !== "") {
    const feetError = validateField(feet, FEET_RANGE, "Feet", "ft");
    if (feetError) return feetError;
  }

  if (inches && inches !== "") {
    // if feet is also provided, inches should be 0-11.9
    if (feet && feet !== "") {
      const inchesError = validateField(inches, INCHES_RANGE, "Inches", "in");
      if (inchesError) return inchesError;
    } else {
      // if only inches provided, check if it's a reasonable value first
      if (parseFloat(inches) < 0 || parseFloat(inches) > 200) {
        return `Inches value is too ${parseFloat(inches) < 0 ? "low" : "high"}`;
      }
    }
  }

  const totalCm = feetInchesToCm(feetNum, inchesNum);

  // Check total height with tolerance and use legacy format
  const tolerance = 0.5;
  if (totalCm > cmRange.MAX + tolerance) {
    const { feet: maxFeet, inches: maxInches } = cmToFeetInches(cmRange.MAX, 0);
    return `Height more than ${maxFeet}' ${maxInches}" entered; please check height. For patients taller than ${maxFeet}' ${maxInches}", seek expert advice.`;
  }
  if (totalCm < cmRange.MIN - tolerance) {
    const { feet: minFeet, inches: minInches } = cmToFeetInches(cmRange.MIN, 0);
    return `Height less than ${minFeet}' ${minInches}" entered; please check height. For patients shorter than ${minFeet}' ${minInches}", seek expert advice.`;
  }

  return "";
};

export const saneSerum = (serum, unit = "micromoles/L") => {
  const num = Number(serum);
  if (isNaN(num)) return `Please enter only numeric characters in the serum creatinine field`;

  const serumMicromol = getCreatinineInMicromol(num, unit);

  if (serumMicromol < SERUM.MIN || serumMicromol > SERUM.MAX) {
    return `The serum creatinine value entered is unusual. Please check it.`;
  }

  return "";
};

export const saneSerumGfr = (serum, unit = "micromoles/L") => {
  const num = Number(serum);
  if (isNaN(num)) return `Please enter only numeric characters in the serum creatinine field`;

  const serumMicromol = getCreatinineInMicromol(num, unit);

  if (serumMicromol < SERUM_GFR.MIN || serumMicromol > SERUM_GFR.MAX) {
    return `The serum creatinine value entered is unusual. Please check it.`;
  }

  return "";
};

export const sanityCheck = (height, weight, heightUnit = "cm", weightUnit = "kg") => {
  const errors = {
    height: validateField(
      height,
      heightUnit === "cm" ? HEIGHT_CM : HEIGHT_IN,
      "Height",
      heightUnit,
    ),
    weight: validateField(weight, weightUnit === "kg" ? WEIGHT : WEIGHT_LBS, "Weight", weightUnit),
  };

  return errors.height === "" && errors.weight === "" ? true : errors;
};

export const sanityCheckGfr = (
  height,
  creatinineValue,
  heightUnit = "cm",
  creatinineUnit = "micromoles/L",
) => {
  const heightError = saneHeight(
    height,
    heightUnit,
    heightUnit === "cm" ? HEIGHT_GFR_CM : HEIGHT_GFR_IN,
  );

  const creatinineError = saneSerumGfr(creatinineValue, creatinineUnit);

  const errors = {
    height: heightError,
    creatinine: creatinineError,
  };

  const allValid = Object.values(errors).every((v) => v === "");
  return allValid ? true : errors;
};

export const validateHeightFromInputs = (
  heightUnit,
  height,
  feet,
  inches,
  useNormalConstants = false,
) => {
  const heightInCm = getHeightInCm(heightUnit, height, feet, inches);

  if (!heightInCm || heightInCm === 0) {
    return "Please enter a value for height";
  }

  return useNormalConstants
    ? normalHeightValidation(heightInCm, "cm")
    : simpleHeightValidation(heightInCm, "cm");
};

export const validateWeightFromInputs = (weight, weightUnit, useNormalConstants = false) => {
  const numCheck = checkNum(weight, "weight");
  if (numCheck !== "") return numCheck;

  return useNormalConstants
    ? normalActualBodyWeightValidation(weight, weightUnit)
    : simpleActualBodyWeightValidation(weight, weightUnit);
};

export const initialise = (passed) => {
  const str = String(passed);
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Simple validation functions for non-clinical calculators (BSA, IBW, LBW)
// These use relaxed ranges and simple "Very small/large" messaging
export const simpleHeightValidation = (height, unit = "cm") => {
  if (!height) return "Please enter a value for height";
  const num = Number(height);
  if (isNaN(num)) return "Please enter only numeric characters in the height field";

  const range = unit === "cm" ? HEIGHT_CM_RELAXED : HEIGHT_IN_RELAXED;

  if (num < range.MIN) return `Very small height entered`;
  if (num > range.MAX) return `Very large height entered`;

  return "";
};

export const simpleActualBodyWeightValidation = (weight, unit = "kg") => {
  if (!weight) return "Please enter a value for actual body weight";
  const num = Number(weight);
  if (isNaN(num)) return "Please enter only numeric characters in the actual body weight field";

  const range = unit === "kg" ? WEIGHT_RELAXED : WEIGHT_LBS_RELAXED;

  if (num < range.MIN) return `Very small actual weight entered`;
  if (num > range.MAX) return `Very large actual weight entered`;

  return "";
};

// Validation functions using normal constants (for IBW/LBW)
export const normalHeightValidation = (height, unit = "cm") => {
  if (!height) return "Please enter a value for height";
  const num = Number(height);
  if (isNaN(num)) return "Please enter only numeric characters in the height field";

  const range = unit === "cm" ? HEIGHT_CM : HEIGHT_IN;

  if (num < range.MIN) return `Very small height entered`;
  if (num > range.MAX) return `Very large height entered`;

  return "";
};

export const normalActualBodyWeightValidation = (weight, unit = "kg") => {
  if (!weight) return "Please enter a value for actual body weight";
  const num = Number(weight);
  if (isNaN(num)) return "Please enter only numeric characters in the actual body weight field";

  const range = unit === "kg" ? WEIGHT : WEIGHT_LBS;

  if (num < range.MIN) return `Very small actual weight entered`;
  if (num > range.MAX) return `Very large actual weight entered`;

  return "";
};
